// This is a fix for badge issues that uses the admin client to bypass RLS
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

// Helper type to handle badge responses from RPC functions
type BadgeResponse = {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  created_at?: string;
};

/**
 * Ensures the First Post badge exists
 * This function has been modified to use trusted server APIs, avoiding RLS issues
 */
export async function ensureFirstPostBadge(): Promise<string | null> {
  console.log('Ensuring First Post badge exists...');
  
  try {
    // 1. Get the badge details (if it exists)
    const { data: badgeData, error: badgeError } = await supabase
      .rpc('get_badge_by_name', { badge_name: 'First Post' });
    
    if (badgeError) {
      console.error('Error checking First Post badge:', badgeError);
      return null;
    }
    
    // If badge exists, return its ID - cast to our type to handle the structure properly
    if (badgeData) {
      const badge = badgeData as unknown as BadgeResponse;
      if (badge && badge.id) {
        console.log('Found existing First Post badge with ID:', badge.id);
        return badge.id;
      }
    }
    
    // If we got here, we need to ensure the badge exists through a trusted server API
    const { data: newBadgeId, error: createError } = await supabase
      .rpc('ensure_badge_exists', {
        badge_name: 'First Post',
        badge_description: 'Awarded for creating your first post',
        badge_icon: '📝',
        badge_color: 'bg-blue-500'
      });
    
    if (createError) {
      console.error('Error ensuring First Post badge exists:', createError);
      return null;
    }
    
    console.log('Ensured First Post badge exists with ID:', newBadgeId);
    return newBadgeId as string;
  } catch (error) {
    console.error('Unexpected error ensuring First Post badge exists:', error);
    return null;
  }
}

/**
 * Awards the First Post badge to a user
 * Uses the award_badge_to_user RPC function to bypass RLS
 */
export async function awardFirstPostBadgeSafe(userId: string, badgeId: string): Promise<boolean> {
  console.log(`Awarding First Post badge to user ${userId} safely...`);
  
  try {
    // Check if the user already has this badge
    const { data: hasAlready, error: checkError } = await supabase
      .rpc('user_has_badge', { 
        user_id_param: userId, 
        badge_id_param: badgeId 
      });
    
    if (checkError) {
      console.error('Error checking if user has badge:', checkError);
      return false;
    }
    
    // If user already has the badge, we're done
    if (hasAlready) {
      console.log(`User ${userId} already has the First Post badge`);
      return true;
    }
    
    // Award the badge through a trusted server function
    const { data: success, error: awardError } = await supabase
      .rpc('award_badge_to_user', {
        user_id_param: userId,
        badge_id_param: badgeId,
        progress_current_param: 100,
        progress_total_param: 100
      });
    
    if (awardError) {
      console.error('Error safely awarding badge:', awardError);
      return false;
    }
    
    console.log(`Successfully awarded First Post badge to user ${userId}`);
    return true;
  } catch (error) {
    console.error('Unexpected error awarding badge safely:', error);
    return false;
  }
}

/**
 * Fix the First Post badge for a user safely
 * This approach uses RPC functions to bypass RLS issues
 */
export async function fixFirstPostBadgeSafe(userId: string): Promise<boolean> {
  console.log('Starting First Post badge fix safely...');
  
  try {
    // First ensure the badge exists
    const badgeId = await ensureFirstPostBadge();
    
    if (!badgeId) {
      console.error('Failed to ensure First Post badge exists');
      toast({
        title: "Badge Error",
        description: "Could not find or create the First Post badge",
        variant: "destructive",
      });
      return false;
    }
    
    // Award the badge to the user
    const success = await awardFirstPostBadgeSafe(userId, badgeId);
    
    if (!success) {
      toast({
        title: "Badge Award Failed",
        description: "Could not award the First Post badge",
        variant: "destructive",
      });
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Unexpected error fixing First Post badge safely:', error);
    toast({
      title: "Badge Fix Error",
      description: "An unexpected error occurred while fixing the First Post badge",
      variant: "destructive",
    });
    return false;
  }
}

/**
 * Ensures the First Comment badge exists
 * This function uses trusted server APIs to bypass RLS issues
 */
export async function ensureFirstCommentBadge(): Promise<string | null> {
  console.log('Ensuring First Comment badge exists...');
  
  try {
    // 1. Get the badge details (if it exists)
    const { data: badgeData, error: badgeError } = await supabase
      .rpc('get_badge_by_name', { badge_name: 'First Comment' });
    
    if (badgeError) {
      console.error('Error checking First Comment badge:', badgeError);
      return null;
    }
    
    // If badge exists, return its ID - cast to our type to handle the structure properly
    if (badgeData) {
      const badge = badgeData as unknown as BadgeResponse;
      if (badge && badge.id) {
        console.log('Found existing First Comment badge with ID:', badge.id);
        return badge.id;
      }
    }
    
    // If we got here, we need to ensure the badge exists through a trusted server API
    const { data: newBadgeId, error: createError } = await supabase
      .rpc('ensure_badge_exists', {
        badge_name: 'First Comment',
        badge_description: 'Awarded for creating your first comment',
        badge_icon: '💬',
        badge_color: 'bg-purple-500'
      });
    
    if (createError) {
      console.error('Error ensuring First Comment badge exists:', createError);
      return null;
    }
    
    console.log('Ensured First Comment badge exists with ID:', newBadgeId);
    return newBadgeId as string;
  } catch (error) {
    console.error('Unexpected error ensuring First Comment badge exists:', error);
    return null;
  }
}

/**
 * Awards the First Comment badge to a user
 * Uses the award_badge_to_user RPC function to bypass RLS
 */
export async function awardFirstCommentBadgeSafe(userId: string, badgeId: string): Promise<boolean> {
  console.log(`Awarding First Comment badge to user ${userId} safely...`);
  
  try {
    // Check if the user already has this badge
    const { data: hasAlready, error: checkError } = await supabase
      .rpc('user_has_badge', { 
        user_id_param: userId, 
        badge_id_param: badgeId 
      });
    
    if (checkError) {
      console.error('Error checking if user has badge:', checkError);
      return false;
    }
    
    // If user already has the badge, we're done
    if (hasAlready) {
      console.log(`User ${userId} already has the First Comment badge`);
      return true;
    }
    
    // Award the badge through a trusted server function
    const { data: success, error: awardError } = await supabase
      .rpc('award_badge_to_user', {
        user_id_param: userId,
        badge_id_param: badgeId,
        progress_current_param: 100,
        progress_total_param: 100
      });
    
    if (awardError) {
      console.error('Error safely awarding badge:', awardError);
      return false;
    }
    
    console.log(`Successfully awarded First Comment badge to user ${userId}`);
    return true;
  } catch (error) {
    console.error('Unexpected error awarding badge safely:', error);
    return false;
  }
}

/**
 * Fix the First Comment badge for a user safely
 * This approach uses RPC functions to bypass RLS issues
 */
export async function fixFirstCommentBadgeSafe(userId: string): Promise<boolean> {
  console.log('Starting First Comment badge fix safely...');
  
  try {
    // First ensure the badge exists
    const badgeId = await ensureFirstCommentBadge();
    
    if (!badgeId) {
      console.error('Failed to ensure First Comment badge exists');
      toast({
        title: "Badge Error",
        description: "Could not find or create the First Comment badge",
        variant: "destructive",
      });
      return false;
    }
    
    // Award the badge to the user
    const success = await awardFirstCommentBadgeSafe(userId, badgeId);
    
    if (!success) {
      toast({
        title: "Badge Award Failed",
        description: "Could not award the First Comment badge",
        variant: "destructive",
      });
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Unexpected error fixing First Comment badge safely:', error);
    toast({
      title: "Badge Fix Error",
      description: "An unexpected error occurred while fixing the First Comment badge",
      variant: "destructive",
    });
    return false;
  }
} 